import React from 'react';
import { Fragment, useEffect, useState } from 'react';
import { Search } from './icons';
import { Input } from './ui';
import type { ImportProduct } from './lib/import-types';
import {
  fetchImportProductsByGroupId,
  toImportProduct,
} from './lib/import-service';
import { defaultRenderProductLink, type RenderProductLink } from './routing';

export const Product360Search: React.FC<{
  hub?: 'import' | 'export';
  groupId?: string;
  renderProductLink?: RenderProductLink;
}> = ({ hub: _hub, groupId, renderProductLink = defaultRenderProductLink }) => {
  const [query, setQuery] = useState('');
  const [products, setProducts] = useState<ImportProduct[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    const trimmedQuery = query.trim();
    if (!groupId || !trimmedQuery) {
      setProducts([]);
      setLoadFailed(false);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const timer = window.setTimeout(() => {
      setLoading(true);
      setLoadFailed(false);
      fetchImportProductsByGroupId(groupId, {
        search: trimmedQuery,
        limit: 6,
        offset: 0,
        sortBy: 'name',
        sortOrder: 'asc',
      }).then(({ success, products: rows }) => {
        if (cancelled) return;
        setLoading(false);
        if (!success) {
          setProducts([]);
          setLoadFailed(true);
          return;
        }
        setProducts(
          rows.map((row) => toImportProduct(row, row.database_id ?? '')),
        );
      });
    }, 250);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [groupId, query]);

  return (
    <div className='ih-search ih-hub-search'>
      <div className='ih-input-group'>
        <span className='ih-input-group-icon'>
          <Search size={18} />
        </span>
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder='Search products by name, SKU, or HTS'
          aria-label='Search products'
          className='ih-input-search ih-hub-search-input'
        />
      </div>
      {query && (
        <div className='ih-search-popover'>
          {loading ? (
            <div className='ih-search-empty'>Loading products…</div>
          ) : loadFailed ? (
            <div className='ih-search-empty'>Product search is unavailable</div>
          ) : products.length ? (
            products.map((product) => (
              <Fragment
                key={`${product.id}-${
                  product.databaseProductId ?? product.importDatabaseId
                }`}
              >
                {renderProductLink({
                  id: product.id,
                  className: 'ih-search-item',
                  onClick: () => setQuery(''),
                  children: (
                    <>
                      <span className='ih-search-item-body'>
                        <span className='ih-search-item-name'>
                          {product.name}
                        </span>
                        <span className='ih-mono ih-text-xs ih-text-muted'>
                          {product.sku} · {product.htsCode || 'Unclassified'}
                        </span>
                      </span>
                      <span className='ih-badge ih-badge-info'>360</span>
                    </>
                  ),
                })}
              </Fragment>
            ))
          ) : (
            <div className='ih-search-empty'>No matching products</div>
          )}
        </div>
      )}
    </div>
  );
};
